import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../../components/Icon';
import { Modal } from '../../../components/Modal';
import { useAppRoutes } from '../../../hooks/useAppRoutes';
import { useGTM } from '../../../hooks/useGTM';
import { Step, TrackingSchema } from '../../../types';
import { useBriefingState } from '../BriefingState';

interface BriefingHeaderProps {
  step: Step;
}

export const BriefingHeader: FC<BriefingHeaderProps> = ({
  step,
}: BriefingHeaderProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [{ branch, steps }] = useBriefingState();
  const [isModalHidden, setIsModalHidden] = useState(true);
  const stepIndex = useMemo(() => steps.indexOf(step), [steps, step]);
  const { briefingRoute, briefingSelectionRoute } = useAppRoutes();
  const { updateDataLayer } = useGTM();

  const cancelOnClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setIsModalHidden(false);
    },
    [],
  );

  const previousStepOnClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (stepIndex > 0) {
        navigate(`${briefingRoute}/${branch}/${steps[stepIndex - 1]}`);
      } else {
        navigate(briefingSelectionRoute);
      }
    },
    [branch, briefingRoute, briefingSelectionRoute, navigate, stepIndex, steps],
  );

  const onConfirm = () => {
    updateDataLayer({
      event: 'Process Canceled',
      schema: TrackingSchema.process,
      data: {
        stage: 'canceled',
        type: branch,
      },
    });
    navigate(briefingSelectionRoute);
  };

  const onCancel = () => {
    setIsModalHidden(true);
  };

  return (
    <>
      <div className="z-10 flex justify-between flex-shrink-0">
        <button
          className="font-medium text-gray-600 hover:text-blue-100 focus:text-blue-100 leading-2"
          onClick={previousStepOnClick}
        >
          <span className="inline-block w-4 mr-2">
            <Icon name="ArrowNavUp" />
          </span>
          {t(`linksOrButtons:backTo`)}:{' '}
          {steps.indexOf(step) > 0
            ? t([
                `topics:${steps[steps.indexOf(step) - 1]}:name:${branch}`,
                `topics:${steps[steps.indexOf(step) - 1]}:name`,
              ])
            : t('linksOrButtons:backToStart')}
        </button>
        <button
          className="block w-4 cursor-pointer select-none text-gray-600 hover:text-blue-100 focus:text-blue-100"
          onClick={cancelOnClick}
        >
          <div className="w-4 h-4">
            <Icon name="Close" />
          </div>
        </button>
      </div>
      <span className="z-10 block mt-8 text-xs font-medium briefing-header__step-indicator md:mt-12 2xl:mt-16">
        {steps.indexOf(step) + 1} / {steps.length}{' '}
      </span>
      <Modal
        icon="Cancel"
        heading={t('modals:cancelBriefing:prompt')}
        isHidden={isModalHidden}
        confirmText={t('modals:cancelBriefing:confirm')}
        cancelText={t('modals:cancelBriefing:cancel')}
        onConfirm={onConfirm}
        onCancel={onCancel}
      >
        {t('modals:cancelBriefing:content')}
      </Modal>
    </>
  );
};
